// The app's chrome: the Portal header (avatar, wordmark) and the world header (resource strip + menu bar), the world tab,
// the title bar, the bottom nav, the menu drawer.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { art } from "./art";
import type { Village } from "@/api/types";
import { BUILDING_NAMES, RESOURCE_KEYS, RESOURCE_NAMES, type BuildingKey, type ResourceKey } from "@/game/constants";
import { abbrev, fmtNum } from "@/game/format";
import { fmtClock } from "@/game/time";
import { freePopulation } from "@/game/city";
import { badgeCount } from "@/game/incoming";
import { useNow } from "@/state/clock";
import {
  Flourish, IconBell, IconBookmark, IconChevronDown, IconChevronLeft, IconGear, IconGlobe, IconHelp, IconLogout, IconMail, IconMenu, IconPeople, IconScroll, IconTrophy, IconCrossedSwords, Pennant,
} from "./icons";
import { Disc, Medallion, cx } from "./primitives";

// ------------------------------------------------------------------ shell

export const Shell = ({ header, nav, children, className }: { header?: ReactNode; nav?: ReactNode; children: ReactNode; className?: string }) => (
  <div className={cx("shell", className)}>
    <div className="slate-bd" aria-hidden="true" />
    {header}
    <main className={cx("shell-main", !nav && "no-nav")}>{children}</main>
    {nav}
  </div>
);

// ------------------------------------------------------------------ world tab

const WorldTabShape = () => (
  <svg className="wtab-shape" viewBox="0 0 268 38" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id="wtab-face" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#222120" /><stop offset="1" stopColor="#0F0E0D" /></linearGradient>
      {/* another world under attack (Oct 15 2026): the tab's face turns to ember-red (kit.css .wtab.alert) */}
      <linearGradient id="wtab-face-alert" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#5A1A12" /><stop offset="1" stopColor="#2A0D09" /></linearGradient>
      <linearGradient id="wtab-rim" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#8C6A35" /><stop offset="1" stopColor="#E3C27A" /></linearGradient>
    </defs>
    <path d="M0.7 0.7 H267.3 L241.3 37.3 H26.7 Z" fill="url(#wtab-face)" stroke="url(#wtab-rim)" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M9 5.5 H259 L238 33 H30 Z" fill="none" stroke="#4A3A24" strokeWidth="1" />
  </svg>
);

/** `alert`: incoming attacks in the player's other worlds (Ben, Oct 15 2026) — the tab glows red until they're gone. */
export const WorldTab = ({ label, onClick, alert = 0 }: { label: string; onClick?: () => void; alert?: number }) => (
  <button type="button" className={cx("wtab", alert > 0 && "alert")} onClick={onClick}
    aria-label={alert > 0 ? `World: ${label}. Another world is under attack (${alert} incoming). Change world` : `World: ${label}. Change world`}>
    <WorldTabShape />
    <span className="wtab-in">
      <IconGlobe className="ico" />
      <span>{label}</span>
      <IconChevronDown className="chev" />
    </span>
  </button>
);

// ------------------------------------------------------------------ avatar

export const Avatar = ({ level, onClick, label = "Your profile" }: { level?: ReactNode; onClick?: () => void; label?: string }) => (
  <button type="button" className="avatar" onClick={onClick} aria-label={label}>
    <Pennant className="pennant" />
    <Medallion className="medal" src={art.avatar} alt="" glow />
    {level !== undefined && level !== null && level !== "" ? <span className="lvl">{level}</span> : null}
  </button>
);

// ------------------------------------------------------------------ headers

export interface HeaderCommon {
  worldLabel: string;
  onWorld?: () => void;
  /** incoming attacks in the player's other worlds: the World tab glows red */
  worldAlert?: number;
  notifications?: number;
  onBell?: () => void;
  onMenu?: () => void;
  avatarLevel?: ReactNode;
  onAvatar?: () => void;
}

/** Portal and other account-wide screens: a gold wordmark with flourishes and a letter-spaced subtitle. Same content as
 *  before, in the world bar's finish (Ben, Oct 12 2026): the fascia stone, the World tab hanging from the top, the bar's
 *  disc size (kit.css `.thdr`). */
export const TitleHeader = ({ title, subtitle, onSubtitle, subtitleLabel, onBack, ...c }: HeaderCommon & { title: string; subtitle: ReactNode; /** makes the subtitle a picker (Empire: the current city with the switcher chevron) */ onSubtitle?: () => void; subtitleLabel?: string; /** a back button in the avatar's place (every account screen but the Portal) */ onBack?: () => void }) => (
  <header className="hdr thdr">
    <div className="hdr-rule" />
    <WorldTab label={c.worldLabel} onClick={c.onWorld} alert={c.worldAlert} />
    <span className="gem" />
    {/* the profile picture is the Portal's alone; the other account screens (Settings, Worlds, Help) carry a back button there */}
    {onBack ? <Disc className="hdr-back" label="Back" onClick={onBack}><IconChevronLeft /></Disc> : <Avatar level={c.avatarLevel} onClick={c.onAvatar} />}
    <div className="ttl">
      <div className="wordmark">
        <Flourish mirror className="flourish" />
        <h1>{title}</h1>
        <Flourish className="flourish" />
      </div>
      {onSubtitle ? (
        <button type="button" className="sub picker" onClick={onSubtitle} aria-label={subtitleLabel}>
          <span className="txt">{subtitle}</span>
          <IconChevronDown />
        </button>
      ) : <div className="sub">{subtitle}</div>}
    </div>
    <HeaderDiscs {...c} />
  </header>
);

/** World screens (Classic Frame, Oct 10 2026 — the live game's layout in the slate finish): the menu bar — Reports and
 *  Mail left of the city box, Announcements and the menu right — with the World tab hanging from the top, then the
 *  resource strip under it (Ben, Oct 10: resources and clock below the bar). In the city box the hamburger at the left
 *  opens the city switcher (from the top), as in the live game; the rest of the box (name and line) goes to the city. No avatar: Portal only. */
export const WorldHeader = ({ city, meta, onCity, onCityName, v, flags = {}, onReports, onMail, strip = true, incoming = 0, onIncoming, onBuilding, ...c }: HeaderCommon & {
  city: string; meta: ReactNode; onCity?: () => void; onCityName?: () => void; v?: Village;
  /** the resource strip under the bar (Store keeps the bar but not the strip — Ben, Oct 11 2026) */
  strip?: boolean;
  /** village/get's new_report / new_mail / new_notifications flags (the server sends flags, not counts) */
  flags?: { reports?: boolean; mail?: boolean; news?: boolean };
  onReports?: () => void; onMail?: () => void;
  /** every incoming attack across the player's cities (Ben, Oct 14 2026): a red ⚔ counter on the city box's corner,
   *  which opens the switcher sorted by incoming; the box glows red while anything is coming */
  incoming?: number; onIncoming?: () => void;
  /** a tap on the resource strip (anything but the clock) opens that building in the current city (Ben, Oct 14 2026) */
  onBuilding?: (key: BuildingKey) => void;
}) => (
  <header className={cx("hdr whdr", !strip && "nostrip")}>
    <div className="whdr-bar">
      <WorldTab label={c.worldLabel} onClick={c.onWorld} alert={c.worldAlert} />
      {/* new report / mail: the icon glows with firelight (Ben, Oct 13 2026), the dot stays */}
      <Disc className={cx("whdr-reports", flags.reports && "glow")} label={flags.reports ? "Reports — new" : "Reports"} onClick={onReports}>
        <IconScroll />{flags.reports && <span className="badge dot" />}
      </Disc>
      <Disc className={cx("whdr-mail", flags.mail && "glow")} label={flags.mail ? "Mail — new" : "Mail"} onClick={onMail}>
        <IconMail />{flags.mail && <span className="badge dot" />}
      </Disc>
      <div className={cx("whdr-city", incoming > 0 && "inc")}>
        <button type="button" className="whdr-city-menu" onClick={onCity} aria-label="Change city" title="Change city"><IconMenu /></button>
        {/* the whole name-and-line area is one button to the city (Ben, Oct 12 2026); the hamburger stays the switcher */}
        <button type="button" className="whdr-city-txt whdr-city-go" onClick={onCityName ?? onCity} aria-label={`Go to ${city}`} title={`Go to ${city}`}>
          <h1>{city}</h1>
          <span className="whdr-city-meta">{meta}</span>
        </button>
        {incoming > 0 && (
          <button type="button" className="whdr-inc" onClick={onIncoming ?? onCity} aria-label={`${incoming} incoming ${incoming === 1 ? "attack" : "attacks"} — show them`} title="Incoming attacks">
            <IconCrossedSwords /><b>{badgeCount(incoming)}</b>
          </button>
        )}
      </div>
      <Disc className="whdr-news" label={flags.news ? "Announcements — new" : "Announcements"} onClick={c.onBell}>
        <IconBell />{flags.news && <span className="badge dot" />}
      </Disc>
      <Disc className="whdr-menu" label="Menu" onClick={c.onMenu}>
        <IconMenu />
      </Disc>
    </div>
    {strip && <ResourceStrip v={v} onBuilding={onBuilding} />}
  </header>
);

/** Where each strip item leads: wood → Lumber Mill, clay → Quarry, iron → Iron Mine, storage → Warehouse, population → Farm. */
export const STRIP_BUILDING: Record<ResourceKey | "storage" | "pop", BuildingKey> = { r_w: "b_w", r_c: "b_c", r_i: "b_i", storage: "b_wh", pop: "b_f" };

/** One strip item: a button to its building when `onGo` is given, a plain span otherwise. Declared at module level so the
 *  strip's clock tick doesn't remount it (a new component type each render made iOS Safari flash the icons). */
const StripItem = ({ to, onGo, className, title, children }: { to: BuildingKey; onGo?: (key: BuildingKey) => void; className?: string | false; title: string; children: ReactNode }) =>
  onGo ? (
    <button type="button" className={cx("rs-item rs-go", className)} title={title} aria-label={`${title} — open ${BUILDING_NAMES[to]}`} onClick={() => onGo(to)}>{children}</button>
  ) : <span className={cx("rs-item", className)} title={title}>{children}</span>;

/** The live game's status strip: wood · clay · iron · storage · population, and the server clock. A full store turns
 *  its number crimson; a boosted resource keeps its bolt. With `onBuilding`, every item but the clock is a button to its
 *  building (STRIP_BUILDING). */
export const ResourceStrip = ({ v, onBuilding }: { v?: Village; onBuilding?: (key: BuildingKey) => void }) => {
  const now = useNow(1000);
  const cap = v?.resources_capacity ?? 0;
  const boosted = (r: ResourceKey) => !!v?.active_effects?.[`boost_${r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"}`];
  return (
    <div className="rstrip" role="group" aria-label="Resources">
      {RESOURCE_KEYS.map((r) => (
        <StripItem key={r} onGo={onBuilding} to={STRIP_BUILDING[r]} className={v && (v.resources?.[r] ?? 0) >= cap && cap > 0 && "full"} title={`${RESOURCE_NAMES[r]} ${fmtNum(v?.resources?.[r])} · ${fmtNum(v?.resources_production?.[r])}/hr`}>
          <img src={art.hud[r === "r_w" ? "wood" : r === "r_c" ? "clay" : "iron"]} alt={RESOURCE_NAMES[r]} />
          {boosted(r) && <i className="bolt" aria-label="boosted" />}
          <b>{v ? abbrev(v.resources?.[r]) : "—"}</b>
        </StripItem>
      ))}
      <StripItem onGo={onBuilding} to={STRIP_BUILDING.storage} title={`Warehouse capacity ${fmtNum(cap)}`}>
        <img src={art.icon.castle} alt="Storage" />
        <b>{v ? abbrev(cap) : "—"}</b>
      </StripItem>
      {/* the farm: population still free to use (Ben, Oct 10 2026), not used / total; crimson when none is left */}
      <StripItem onGo={onBuilding} to={STRIP_BUILDING.pop} className={v && freePopulation(v) === 0 && "full"} title={`Free population ${fmtNum(v ? freePopulation(v) : undefined)} · ${fmtNum(v?.current_population)} of ${fmtNum(v?.max_population)} used`}>
        <img src={art.hud.food} alt="Free population" />
        <b>{v ? abbrev(freePopulation(v)) : "—"}</b>
      </StripItem>
      <span className="rs-clock" aria-label="Server time, UTC" title="Server time (UTC, the same for every player)">{fmtClock(now, true)}<small className="rs-tz">UTC</small></span>
    </div>
  );
};

/** The live game's title bar: round back · TITLE · round action (gear, quill…), full width under the header. */
export const TitleBar = ({ title, level, sub, onBack, action }: {
  title: ReactNode; level?: number | null; sub?: ReactNode; onBack?: () => void;
  action?: { icon?: ReactNode; label: string; onClick?: () => void };
}) => (
  <div className="tbar">
    {onBack ? <Disc size="sm" label="Back" onClick={onBack}><IconChevronLeft /></Disc> : <span className="tbar-sp" />}
    <div className="tbar-mid">
      <h1>{level !== undefined && level !== null && <span className="c-lvl sm">{level}</span>}<span className="t">{title}</span></h1>
      {sub && <div className="tbar-sub">{sub}</div>}
    </div>
    {action ? <Disc size="sm" label={action.label} onClick={action.onClick}>{action.icon ?? <IconGear />}</Disc> : <span className="tbar-sp" />}
  </div>
);

/** Portal and the account screens: the bell (announcements) and the menu. */
const HeaderDiscs = ({ notifications, onBell, onMenu }: HeaderCommon) => (
  <>
    <Disc className="bell" label="Announcements" onClick={onBell}>
      <IconBell />{notifications ? <span className="badge dot" /> : null}
    </Disc>
    <Disc className="menu" label="Menu" onClick={onMenu}>
      <IconMenu />
    </Disc>
  </>
);

// ------------------------------------------------------------------ bottom nav

export interface NavBadges {
  portal?: number;
  quests?: number;
  map?: number;
  guild?: number;
  store?: number;
}

/** Which bottom tab a path belongs to (the Store tab also covers a city's Store, where /w/:wid/store redirects). */
export const navTabOf = (path: string): "portal" | "quests" | "map" | "guild" | "store" | null => {
  if (/^\/portal(\/|$)/.test(path)) return "portal";
  if (/^\/w\/\d+\/quests(\/|$)/.test(path)) return "quests";
  if (/^\/w\/\d+\/map(\/|$)/.test(path)) return "map";
  if (/^\/w\/\d+\/guild(\/|$)/.test(path)) return "guild";
  if (/^\/w\/\d+\/(city\/\d+\/)?store(\/|$)/.test(path)) return "store";
  return null;
};

/** Five equal adjoining text tabs — Portal · Quests · Map · Guild · Store (Fire & Slate Rev. 11 §6). Badges carry real
 *  counts only: claimable quests on Quests, incoming attacks on Map. The selected tab is the main light source. */
export const BottomNav = ({ wid, badges = {} }: { wid: number | null; badges?: NavBadges }) => {
  const w = wid ? `/w/${wid}` : null;
  const { pathname } = useLocation();
  const active = navTabOf(pathname);
  const items: { key: NonNullable<ReturnType<typeof navTabOf>>; to: string; label: string; badge?: number }[] = [
    { key: "portal", to: "/portal", label: "Portal", badge: badges.portal },
    { key: "quests", to: w ? `${w}/quests` : "/worlds", label: "Quests", badge: badges.quests },
    { key: "map", to: w ? `${w}/map` : "/worlds", label: "Map", badge: badges.map },
    { key: "guild", to: w ? `${w}/guild` : "/worlds", label: "Guild", badge: badges.guild },
    { key: "store", to: w ? `${w}/store` : "/worlds", label: "Store", badge: badges.store },
  ];
  const ref = useRef<HTMLElement>(null);
  return (
    <>
      <nav className="nav" aria-label="Main" ref={ref} data-tab={active ?? undefined}>
        <ul>
          {items.map((it) => (
            <li key={it.key}>
              <Link to={it.to} className={cx(active === it.key && "on")} aria-current={active === it.key ? "page" : undefined}>
                <span>{it.label}</span>
                {it.badge ? <span className="badge" aria-label={`${it.badge} new`}>{it.badge}</span> : null}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      {/* after the nav, so the nav's ref is set when the light measures the selected tab */}
      <ActionLight navRef={ref} active={active} />
    </>
  );
};

/** The screen's main light (Fire & Slate Rev. 11 §4, §11): a restrained warm-bronze reflection and a few lit mineral
 *  edges, anchored to the selected control's actual bounds — the top lip of the selected bottom tile, or the right edge of
 *  the selected rail tile on wide screens — sized to that control and gliding to the next one. It is state, not paint: a
 *  separate layer over the neutral slate and under the translucent faces, so it carries on beneath rows and sections
 *  (kit.css .act-light; strength --stone-light, independent of the metal's --gold-sheen). With no tab selected, a
 *  selected subtab on the screen is a weaker local source (.act-light.weak); with neither, there is no light. */
// Each screen draws its own nav, so the light's last spot is kept here: a new screen starts the light where the previous
// one left it and lets it glide to the new tab (no transition with reduced motion, kit.css / tokens.css).
let lastLight: { x: number; y: number } | null = null;

const ActionLight = ({ navRef, active }: { navRef: React.RefObject<HTMLElement | null>; active: string | null }) => {
  const light = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    let raf = 0;
    const set = (el: HTMLElement, p: { x: number; y: number }) => {
      el.style.setProperty("--lx", `${p.x}px`);
      el.style.setProperty("--ly", `${p.y}px`);
    };
    // the core of the reflection is as wide as the control that casts it (a tile, a rail tile, a subtab)
    const size = (el: HTMLElement, r: DOMRect, rail: boolean) => el.style.setProperty("--sw", `${Math.round(rail ? r.height * 2.2 : r.width)}px`);
    const place = (first = false, instant = false) => {
      const el = light.current;
      if (!el) return;
      const on = navRef.current?.querySelector<HTMLElement>("a.on");
      // no selected bottom tab: a selected subtab on the screen is a weaker, local source (Rev. 11 §4); none → no light
      const sub = on ? null : document.querySelector<HTMLElement>(".shell-main :is(.seg, .tabs) button.on");
      const src = on ?? sub;
      if (!src) { el.classList.add("off"); return; }
      const r = src.getBoundingClientRect();
      const rail = !!on && document.documentElement.classList.contains("lay-rail");
      const to = sub
        ? { x: Math.round(r.left + r.width / 2), y: Math.round(r.top + r.height / 2) }
        : { x: Math.round(rail ? r.right : r.left + r.width / 2), y: Math.round(rail ? r.top + r.height / 2 : r.top) };
      el.classList.remove("off");
      el.classList.toggle("weak", !!sub);
      size(el, r, rail);
      if (first) {
        el.style.transition = "none";
        set(el, sub ? to : lastLight ?? to);
        void el.offsetWidth; // commit the start without a transition
        el.style.transition = "";
        if (!sub && lastLight && (lastLight.x !== to.x || lastLight.y !== to.y)) raf = requestAnimationFrame(() => set(el, to));
      } else if (instant) {
        el.style.transition = "none";
        set(el, to);
        void el.offsetWidth;
        el.style.transition = "";
      } else set(el, to);
      if (!sub) lastLight = to;
    };
    place(true);
    const onResize = () => place();
    // a subtab source scrolls with the page and changes when another subtab is chosen: follow it (no glide while scrolling)
    let pending = 0;
    const follow = () => { cancelAnimationFrame(pending); pending = requestAnimationFrame(() => { if (light.current?.classList.contains("weak") || !navRef.current?.querySelector("a.on")) place(false, true); }); };
    const onClick = () => requestAnimationFrame(follow);
    const ro = new ResizeObserver(onResize);
    if (navRef.current) ro.observe(navRef.current);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", follow, { passive: true });
    document.addEventListener("click", onClick, true);
    return () => {
      cancelAnimationFrame(raf); cancelAnimationFrame(pending); ro.disconnect();
      window.removeEventListener("resize", onResize); window.removeEventListener("scroll", follow); document.removeEventListener("click", onClick, true);
    };
  }, [navRef, active]);
  return <div ref={light} className="act-light off" data-tab={active ?? undefined} aria-hidden="true" />;
};

// ------------------------------------------------------------------ menu drawer

export interface MenuBadges {
  reports?: number;
  mail?: number;
}

export const MenuDrawer = ({ open, onClose, login, email, wid, uid, onSignOut }: {
  open: boolean; onClose: () => void; login?: string | null; email?: string | null; wid: number | null; uid?: number | null; onSignOut: () => void;
}) => {
  const nav = useNavigate();
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const go = (to: string) => () => {
    onClose();
    nav(to);
  };
  const w = wid ? `/w/${wid}` : null;
  return (
    <div className="scrim" onClick={onClose} role="presentation">
      <aside className={cx("drawer", closing && "closing")} role="dialog" aria-label="Menu" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="who" onClick={w && uid ? go(`${w}/player/${uid}`) : go("/worlds")} aria-label="Your profile">
          <Medallion src={art.avatar} alt="" size={72} />
          <div>
            <b>{login || "Signed in"}</b>
            <span>{email || ""}</span>
            <span className="who-link">{w ? "View profile" : "Open a world to see your profile"}</span>
          </div>
        </button>
        <button type="button" className="item" onClick={w ? go(`${w}/guild`) : undefined} disabled={!w}><IconPeople /> Guild</button>
        <button type="button" className="item" onClick={w ? go(`${w}/rank`) : undefined} disabled={!w}><IconTrophy /> Leaderboards</button>
        <button type="button" className="item" onClick={w ? go(`${w}/map?bookmarks=1`) : undefined} disabled={!w}><IconBookmark /> Bookmarks</button>
        <hr />
        <button type="button" className="item" onClick={go("/worlds")}><IconGlobe /> Worlds</button>
        <button type="button" className="item" onClick={go("/settings")}><IconGear /> Settings</button>
        <button type="button" className="item" onClick={go("/help")}><IconHelp /> Help &amp; Terms</button>
        <hr />
        <button type="button" className="item" onClick={() => { setClosing(true); onSignOut(); }}><IconLogout /> Sign out</button>
        <hr />
        <button type="button" className="item" onClick={go("/kit")}><IconGear /> Design kit</button>
      </aside>
    </div>
  );
};
